import axios, { type AxiosError } from 'axios';
import { environment } from '@shared/config';
import type { StoredTokens } from '../model/auth.types';

/** Bound the code-exchange call so a hung IdP doesn't strand the login callback forever. */
const TOKEN_EXCHANGE_TIMEOUT_MS = 15_000;

/**
 * A token exchange the backend refused. Carries the backend's `code` (e.g.
 * `USER_DEACTIVATED`) alongside the raw body in `message`, so the login screen can show
 * the matching copy instead of parsing it back out of a formatted string.
 */
export class TokenExchangeError extends Error {
  readonly code?: string;

  constructor(message: string, code?: string) {
    super(message);
    this.name = 'TokenExchangeError';
    this.code = code;
  }
}

export async function getToken(code: string): Promise<StoredTokens> {
  const payload = new URLSearchParams({ code });

  try {
    // Raw axios (not coreHttp) so the 401 interceptor never fires during login —
    // a token-exchange failure must surface as a login error, not a refresh
    // attempt / "session expired" modal. Same reason as refreshToken below.
    const response = await axios.post<StoredTokens>(environment.tokenEndpoint, payload, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      timeout: TOKEN_EXCHANGE_TIMEOUT_MS,
    });
    return response.data;
  } catch (error) {
    const axiosError = error as AxiosError;
    const status = axiosError.response?.status ?? 'unknown';
    const body = axiosError.response?.data;
    const errorBody =
      typeof body === 'string' ? body : body ? JSON.stringify(body) : axiosError.message;
    const errorCode =
      typeof body === 'object' && body !== null ? (body as { code?: unknown }).code : undefined;
    throw new TokenExchangeError(
      `Token exchange failed (${status}): ${errorBody}`,
      typeof errorCode === 'string' ? errorCode : undefined
    );
  }
}

/** Bound the refresh call so a hung network can't hold the cross-tab refresh lock open. */
const REFRESH_TIMEOUT_MS = 15_000;

/**
 * Refresh access token using a refresh token.
 * Uses raw axios (not coreHttp) to avoid triggering the 401 interceptor loop.
 */
export async function refreshToken(refreshTokenValue: string): Promise<StoredTokens> {
  const response = await axios.post<StoredTokens>(
    `${environment.apiUrl}/v1/auth/refresh`,
    { refresh_token: refreshTokenValue },
    { headers: { 'Content-Type': 'application/json' }, timeout: REFRESH_TIMEOUT_MS }
  );
  return response.data;
}
